<template>
  <mep-input-field
    v-bind="$attrs"
    :label="label"
    :error="computedError"
    :disabled="disabled"
    :is-focused="isFocused"
    :has-content="hasContent || !!placeholder"
    :input-id="inputId"
    :label-id="labelId"
  >
    <input
      :id="inputId"
      ref="input"
      v-model="model"
      :type="type"
      :disabled="disabled"
      :aria-labelledby="labelId"
      class="w-full pt-4 text-base font-normal leading-6 tracking-[0.5px] outline-none disabled:cursor-not-allowed disabled:opacity-60"
      :placeholder
      @focus="handleFocus"
      @blur="handleBlur"
    />

    <template #dropdown>
      <slot name="dropdown" />
    </template>
  </mep-input-field>
</template>

<script setup lang="ts">
import { computed, InputTypeHTMLAttribute, provide, useTemplateRef } from 'vue';
import { useInputField } from './MepInputField/useInputField';
import { MepInputReference } from './MepInputField/symbols';

interface Props {
  label: string;
  placeholder?: string;
  error?: string | string[] | null;
  disabled?: boolean;
  type?: InputTypeHTMLAttribute;
}

const props = withDefaults(defineProps<Props>(), {
  error: undefined,
  disabled: false,
  placeholder: undefined,
  type: 'text',
});

const emit = defineEmits<{
  (e: 'focus'): void;
}>();

const input = useTemplateRef('input');

const model = defineModel<string | undefined>();

const { inputId, labelId, isFocused, focusedBefore, hasContent, handleFocus, handleBlur } = useInputField(model, emit.bind(null, 'focus'));

// Only show error if the field has been focused at least once
// This prevents showing errors on initial render before user interaction
const computedError = computed(() => {
  if (!focusedBefore.value) return null;
  return props.error;
});

provide(MepInputReference, input);
</script>
<style scoped>
input {
  background: none;
  border: none;
  appearance: none;
  border-radius: 0;
  padding: 0;
  padding-top: 16px;
}

input:focus {
  outline: none;
  border-color: inherit;
  -webkit-box-shadow: none;
  box-shadow: none;
}

input:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}
</style>
